import md5 from 'blueimp-md5'

import type { Address } from '@app/packages/geo/Address'
import { createAddress } from '@app/packages/geo/Address'
import { isValidLatitude } from '@app/packages/geo/Point'

export class AddressSerializer {
  static serialize(address: Address) {
    const data = [
      AddressSerializer.maybeFloatToString(address.location.lat),
      AddressSerializer.maybeFloatToString(address.location.lon),
      AddressSerializer.escape(address.label ?? ''),
      AddressSerializer.escape(address.kind ?? ''),
      AddressSerializer.maybeFloatToString(address.bounds?.bottomLeft.lat),
      AddressSerializer.maybeFloatToString(address.bounds?.bottomLeft.lon),
      AddressSerializer.maybeFloatToString(address.bounds?.topRight.lat),
      AddressSerializer.maybeFloatToString(address.bounds?.topRight.lon),
      ...(address.isPoint ? [POINT_FLAG] : []),
    ].join(':')
    const signature = AddressSerializer.sign(data)
    return [data, signature].join(':')
  }

  static deserialize(value: string) {
    const parts = value.split(/:/)
    if (parts.length !== 9 && parts.length !== 10) return null
    const data = parts.slice(0, -1)
    const signature = AddressSerializer.sign(data.join(':'))
    if (signature !== parts[parts.length - 1]) return null
    const address = createAddress({
      isPoint: data[8] === POINT_FLAG ? true : undefined,
      location: {
        lat: parseFloat(data[0]),
        lon: parseFloat(data[1]),
      },
      label: data[2] ? AddressSerializer.unescape(data[2]) : undefined,
      kind: data[3] ? AddressSerializer.unescape(data[3]) : undefined,
      bounds:
        data[4] === '' || data[5] === '' || data[6] === '' || data[7] === ''
          ? undefined
          : {
              bottomLeft: {
                lat: parseFloat(data[4]),
                lon: parseFloat(data[5]),
              },
              topRight: {
                lat: parseFloat(data[6]),
                lon: parseFloat(data[7]),
              },
            },
    })
    // The signing key ships in the bundle, so a signed value proves nothing about its coordinates.
    const points = [address.location, ...(address.bounds ? [address.bounds.bottomLeft, address.bounds.topRight] : [])]
    return points.every(p => isValidLatitude(p.lat)) ? address : null
  }

  static maybeFloatToString(value: number | undefined) {
    return (value ?? '') === '' ? '' : String(value)
  }

  private static escape(value: string) {
    return value.replace(/%/g, '%25').replace(/:/g, '%3A')
  }

  private static unescape(value: string) {
    return value.replace(/%3A/g, ':').replace(/%25/g, '%')
  }

  private static sign(value: string) {
    return md5(value, 'kidsout').slice(24)
  }
}

/** A ninth field exists only for a point, so every value signed before it stays valid. */
const POINT_FLAG = 'point'
